import { useState } from "react";
import { Group, Paper, Text } from "@mantine/core";
import { AiOutlinePaperClip } from "react-icons/ai";
import { fetchAttachmentUrl } from '../services/MessagesService';
import { showErrorNotifications } from "../../../components/ServiceNotifications.tsx";

interface Attachment {
    filename: string;
}

interface AttachmentsListProps {
    messageId: string;
    attachments: Attachment[];
}

/**
 * FNP-1532: attachments download through an API-minted URL.
 *
 * This component used to call `fetchAuthSession()`, build
 * `private/${identityId}/${filePath}` in the browser, and hand that to Amplify
 * `getUrl`. It was the clearest example of the prefix-as-authorization pattern
 * FNP-877 set out to remove: the client decided which key to read, and the only
 * thing standing between a user and another tenant's mail was the client's own
 * good manners plus a bucket-wide IAM grant (FNP-884).
 *
 * Now the server resolves the key from the stored message after checking alias
 * ownership. The browser holds no AWS credentials and never sees a key.
 *
 * ⚠️ The URL is minted ON CLICK, never while rendering the list. Minting per row
 * would turn one render into N signatures — FNP-877's "one S3 read per row"
 * problem wearing a different hat — and every one of those is a live bearer
 * credential for a file nobody asked for.
 *
 * ⚠️ The selector is the ARRAY INDEX, not the filename. Filenames are not unique
 * within a message (FNP-1534), so a filename lookup has no single answer.
 */
export const AttachmentsList = ({ messageId, attachments }: AttachmentsListProps) => {
    const [busyIndex, setBusyIndex] = useState<number | null>(null);

    const handleAttachmentClick = async (index: number) => {
        if (busyIndex !== null) {
            return;
        }

        setBusyIndex(index);
        try {
            const minted = await fetchAttachmentUrl(messageId, index);

            if (!minted?.url) {
                showErrorNotifications(new Error('This attachment could not be opened.'));
                return;
            }

            // Open immediately: the URL is short-lived by design, so anything
            // that defers the navigation risks handing over a dead link.
            window.open(minted.url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            // Deliberately does not log the error object at debug level the way
            // the old code logged the URL — a failure here can carry the signed
            // URL in its request context, and that URL is the credential.
            console.error('Failed to open attachment');
            showErrorNotifications(error);
        } finally {
            setBusyIndex(null);
        }
    };

    if (!attachments || attachments.length === 0) {
        return null;
    }

    return (
        <Group p={'sm'}>
            {attachments.map((attachment, index) => (
                <Paper
                    key={index}
                    withBorder
                    p={'md'}
                    shadow={'sm'}
                    onClick={() => handleAttachmentClick(index)}
                    style={{ cursor: busyIndex === null ? 'pointer' : 'progress' }}
                >
                    <Group>
                        <AiOutlinePaperClip size={'20'} />
                        <Text>{attachment.filename}</Text>
                    </Group>
                </Paper>
            ))}
        </Group>
    );
};
